import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import PlaylistArticle from "./PlaylistArticle";
import ArticleLoader from "./ArticleLoader";
import loadingIcon from "../../../Assets/loading.gif";
import store from "../store";
// import Intercom from '@intercom/messenger-js-sdk';
import Loader from "../../../Components/Loader";
import { isMobile } from 'react-device-detect';
import { Download as DownloadIcon, Share as ShareIcon, MoreVert as MoreVertIcon, Help as HelpIcon } from '@mui/icons-material';
import { IconButton, Tooltip, Menu, MenuItem } from '@mui/material';
import Play from '../Icons/Play.svg';
import Pause from '../Icons/Pause.svg';
import { getFeed, createReactivationCheckoutSession } from '../../../audiblogsApi';
import { isPaidProUser } from '../../../constants/plans';
import userStore from '../../../Stores/User';
import { Modal, mergeStyleSets } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';

// Cache for feed titles
const feedTitleCache = new Map();

// Modal styles
const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column',
    backgroundColor: 'rgb(66 59 74)',
    width: '300px',
    padding: '10px',
    fontFamily: 'Cabin',
    fontSize: '18px',
    textAlign: 'center',
    minHeight: '108px',
    borderRadius: '14px',
  },
});

// Duration calculation function
const estimatedMinutes = async (audioUrl) => {
  if (!audioUrl) return 1;

  try {
    const audio = new Audio();
    audio.preload = 'metadata'; // Only load metadata, not the full audio
    
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        audio.removeEventListener('loadedmetadata', handleMetadata);
        audio.removeEventListener('error', handleError);
        resolve(1);
      }, 5000); // 5 second timeout

      const handleMetadata = () => {
        clearTimeout(timeout);
        const minutes = Math.ceil(audio.duration / 60);
        resolve(Math.max(1, minutes));
      };

      const handleError = () => {
        clearTimeout(timeout);
        resolve(1);
      };

      audio.addEventListener('loadedmetadata', handleMetadata);
      audio.addEventListener('error', handleError);
      audio.src = audioUrl;
    });
  } catch (error) {
    console.error('Error getting audio duration:', error);
    return 1;
  }
};

const Articles = observer(
  ({
    articles,
    trackingId,
    addArticleToPlaylist,
    selectArticle,
    togglePlayed,
    removeArticle,
    changeMode,
  }) => {
    // Add CSS animation for pulse effect
    useEffect(() => {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes pulse {
          0% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.02);
          }
          100% {
            opacity: 0.6;
            transform: scale(1);
          }
        }
      `;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }, []);
    
    // Modal state for upgrade prompts
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const [modalContent, setModalContent] = useState(null);
    
    // Check if user is pro
    const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
      ? isPaidProUser(userStore.user)
      : false;
    
    const { currentArticle, isPlaying, playlistMode, playlistSearchQuery } = store;
    const [visibleArticles, setVisibleArticles] = useState([]);
    const loadMoreRef = useRef(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Track window width for responsive design
    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isDesktop = windowWidth > 1200;

    // Handle upgrade modal for non-pro users
    const showUpgradeModal = () => {
      setModalContent({
        title: "Pro Feature", 
        message: "Upgrade to pro to download articles.",
        actionText: "Upgrade Now",
        actionHandler: () => {
          const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
          const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
          
          if (hasStripeCustomerId && !hasActiveSubscription) {
            // User has cancelled subscription - create reactivation checkout
            createReactivationCheckoutSession(userStore.idToken, {}, '/add')
              .then(response => {
                if (response.data.url) {
                  window.location.href = response.data.url;
                } else {
                  window.location.href = '/onboarding/choosevoice';
                }
              })
              .catch(() => {
                window.location.href = '/onboarding/choosevoice';
              });
          } else {
            // User has active subscription - send to customer portal
            window.location.href = userStore.customerPortalUrl;
          }
          hideModal();
        }
      });
      showModal();
    };

    // useEffect(() => {
    //   Intercom({
    //     app_id: "fduylg2r", 
    //     hide_default_launcher: false,
    //     mobile: true,
    //   });
    // }, []);

    useEffect(() => {
      if (!articles) return;
      // Always update visible articles when articles change
      setVisibleArticles(prev => {
        // Keep same number of visible articles but with updated content
        const numVisible = prev.length || 10;
        return articles.slice(0, numVisible);
      });
    }, [articles]); // Depend on articles, not just length

    useEffect(() => {
      if (!articles) return;
      
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting) {
            setVisibleArticles(prev => {
              if (prev.length >= articles.length) return prev;
              const nextBatch = articles.slice(prev.length, prev.length + 10);
              return [...prev, ...nextBatch];
            });
          }
        },
        { threshold: 0.1 }
      );

      const currentRef = loadMoreRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
        observer.disconnect();
      };
    }, [articles]); // Depend on articles, not just length

    // Filtering logic
    let filteredArticles = articles || [];
    
    // Apply playlist mode filter
    if (playlistMode === 'played') {
      filteredArticles = filteredArticles.filter(a => a.played);
    } else if (playlistMode === 'unplayed') {
      filteredArticles = filteredArticles.filter(a => !a.played);
    }
    
    // Apply search filter
    if (playlistSearchQuery.trim()) {
      const query = playlistSearchQuery.toLowerCase().trim();
      filteredArticles = filteredArticles.filter(article => 
        article.title && article.title.toLowerCase().includes(query)
      );
    }

    // Desktop table header component
    const DesktopTableHeader = () => (
      <div className="desktop-table-header" style={{
        display: 'grid',
        gridTemplateColumns: '50px 1fr 80px 120px 150px 120px',
        gap: '24px',
        padding: '16px',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: '16px',
        width: '100%',
        maxWidth: '1200px'
      }}>
        <div></div>
        <div>TITLE</div>
        <div>DURATION</div>
        <div>VOICE</div>
        <div>FEED</div>
        <div></div>
      </div>
    );

    const DesktopArticleLoader = () => (
      <div className="desktop-article-loader" style={{
        display: 'grid',
        gridTemplateColumns: '50px 1fr 80px 120px 150px 120px',
        gap: '24px',
        padding: '16px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        marginBottom: '8px',
        alignItems: 'center',
        width: '100%',
        maxWidth: '1200px'
      }}>
        {/* Thumbnail Column */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
        </div>

        {/* Title Column */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '20px',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              height: '16px',
              background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
              animation: 'pulse 2s ease-in-out infinite',
              borderRadius: '4px',
              marginBottom: '4px'
            }} />
            <div style={{
              height: '12px',
              width: '60%',
              background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
              animation: 'pulse 2s ease-in-out infinite',
              borderRadius: '3px'
            }} />
          </div>
        </div>

        {/* Duration Column */}
        <div style={{
          height: '14px',
          width: '40px',
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          animation: 'pulse 2s ease-in-out infinite',
          borderRadius: '3px'
        }} />

        {/* Voice Column */}
        <div style={{
          height: '14px',
          width: '60px',
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          animation: 'pulse 2s ease-in-out infinite',
          borderRadius: '3px'
        }} />

        {/* Feed Column */}
        <div style={{
          height: '14px',
          width: '80px',
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          animation: 'pulse 2s ease-in-out infinite',
          borderRadius: '3px'
        }} />

        {/* Actions Column */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '18px',
            height: '18px',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            animation: 'pulse 2s ease-in-out infinite',
            borderRadius: '3px'
          }} />
          <div style={{
            width: '18px',
            height: '18px',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            animation: 'pulse 2s ease-in-out infinite',
            borderRadius: '3px'
          }} />
          <div style={{
            width: '18px',
            height: '18px',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            animation: 'pulse 2s ease-in-out infinite',
            borderRadius: '3px'
          }} />
        </div>
      </div>
    );

    // Desktop table row component
    const DesktopTableRow = ({ article }) => {
      const isActive = isPlaying && currentArticle && currentArticle.article_id === article.article_id;
      const [duration, setDuration] = useState(article.duration || 1);
      const [menuAnchorEl, setMenuAnchorEl] = useState(null);
      const [feedTitle, setFeedTitle] = useState(null);
      
      // Calculate duration from audio URL
      useEffect(() => {
        if (article.audio_url) {
          estimatedMinutes(article.audio_url)
            .then(mins => setDuration(mins))
            .catch(err => {
              console.error('Error getting duration:', err);
              setDuration(1);
            });
        }
      }, [article.audio_url]);
      
      // Load feed title with caching
      useEffect(() => {
        if (article.feed_id) {
          // Check cache first
          const cachedTitle = feedTitleCache.get(article.feed_id);
          if (cachedTitle) {
            setFeedTitle(cachedTitle);
            return;
          }

          // If not in cache, fetch from backend
          getFeed(article.feed_id)
            .then(({ feed }) => {
              const title = feed.title;
              feedTitleCache.set(article.feed_id, title); // Store in cache
              setFeedTitle(title);
            })
            .catch(err => {
              console.error('Error fetching feed:', err);
            });
        }
      }, [article.feed_id]);
      
      const downloadArticle = async () => {
        if (!article.audio_url) return;
        
        try {
          const response = await fetch(article.audio_url);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${article.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp3`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } catch (error) {
          console.error('Error downloading article:', error);
        }
      };

      const shareArticle = () => {
        const audioId = article.audio_url.split('/').pop().replace('.mp3', '');
        const shareUrl = `${window.location.origin}/share/${audioId}`;
        if (navigator.share) {
          navigator.share({
            title: article.title,
            url: shareUrl,
          });
        } else {
          navigator.clipboard.writeText(shareUrl);
        }
      };

      const deleteArticle = () => {
        setMenuAnchorEl(null);
        removeArticle(article);
      };

      const togglePlayed = () => {
        setMenuAnchorEl(null);
        togglePlayed(article);
      };

      const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
      };

      const handleMenuClose = () => {
        setMenuAnchorEl(null);
      };
      
      return (
        <div className="desktop-table-row" style={{
          display: 'grid',
          gridTemplateColumns: '50px 1fr 80px 120px 150px 120px',
          gap: '24px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          marginBottom: '8px',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
          e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
          e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.05)';
        }}
                onClick={(e) => {
          // Don't select article if clicking on interactive elements
          if (e.target.closest('[data-interactive]')) {
            return;
          }
          if (article.status !== 'failed') {
            selectArticle(article);
          }
        }}>
          {/* Thumbnail Column */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {article.thumbnail_url ? (
              <img 
                src={article.thumbnail_url} 
                alt={article.title} 
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '6px',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <img 
                src="/logo64.png" 
                alt="Audioread" 
                style={{ 
                  width: '32px', 
                  height: '32px',
                  objectFit: 'contain'
                }} 
              />
            )}
          </div>

          {/* Title Column */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
            <div 
              data-interactive="true"
              style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '20px', 
                background: 'rgba(255, 255, 255, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (isActive) {
                  // If currently playing this article, pause it
                  selectArticle(article);
                } else {
                  // If not playing this article, start playing it
                  selectArticle(article);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <img 
                src={isActive ? Pause : Play} 
                alt={isActive ? "Pause" : "Play"} 
                style={{ width: '16px', height: '16px' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}>
                {article.title}
              </div>
              {article.status !== 'completed' && (
                <div style={{ 
                  fontSize: '13px', 
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginTop: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {article.status === 'in_progress' ? (
                    <>
                      <img
                        src={loadingIcon}
                        style={{ height: 15, marginRight: '5px' }}
                      />
                      {article.status_details || 'Processing article...'}
                    </>
                  ) : article.status === 'pending' ? (
                    <>
                      <img
                        src={loadingIcon}
                        style={{ height: 15, marginRight: '5px' }}
                      />
                      Queued for processing...
                    </>
                  ) : article.status === 'failed' ? (
                     <div style={{
                       display: 'flex',
                       alignItems: 'center',
                       gap: '8px'
                     }}>
                       <span>Audioreading Failed</span>
                       <Tooltip title="Learn how to troubleshoot failed conversions">
                         <a 
                           href="https://help.audioread.com/en/articles/11893570-what-to-do-when-audioreading-fails"
                           target="_blank"
                           rel="noopener noreferrer"
                           style={{ 
                             color: '#757575',
                             display: 'flex',
                             alignItems: 'center',
                             padding: '4px',
                             borderRadius: '50%',
                             transition: 'all 0.2s ease',
                             textDecoration: 'none',
                             fontSize: '16px'
                           }}
                           onMouseEnter={(e) => {
                             e.currentTarget.style.color = '#1976d2';
                             e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
                           }}
                           onMouseLeave={(e) => {
                             e.currentTarget.style.color = '#757575';
                             e.currentTarget.style.backgroundColor = 'transparent';
                           }}
                           onClick={(e) => e.stopPropagation()}
                         >
                           <HelpIcon fontSize="small" />
                         </a>
                       </Tooltip>
                     </div>
                   ) : ''}
                </div>
              )}
            </div>
          </div>

          {/* Duration Column */}
          <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px', fontWeight: '500' }}>
            {duration} min
          </div>

          {/* Voice Column */}
          <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
            {(article.voice_name && article.voice_name !== 'Not saved') || (article.voice && article.voice !== 'Not saved') ? 
              (article.voice_name || article.voice) : 
              <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Default</span>
            }
          </div>

          {/* Feed Column */}
          <div style={{ 
            color: 'rgba(255, 255, 255, 0.6)', 
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {article.feed_id ? 
              (feedTitle ? 
                (feedTitle.length > 20 ? feedTitle.substring(0, 20) + '...' : feedTitle) :
                <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontStyle: 'italic' }}>Loading...</span>
              ) : 
              <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Manual</span>
            }
          </div>

          {/* Actions Column */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={
              article.status === 'completed' && article.audio_url 
                ? (isProUser ? "Download" : "Upgrade to pro to download") 
                : "Download not available"
            }>
              <DownloadIcon 
                data-interactive={article.status === 'completed' && article.audio_url ? "true" : "false"}
                style={{ 
                  fontSize: '18px', 
                  color: article.status === 'completed' && article.audio_url 
                    ? (isProUser ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.2)')
                    : 'rgba(255, 255, 255, 0.2)',
                  cursor: article.status === 'completed' && article.audio_url ? 'pointer' : 'default'
                }}
                onClick={(e) => {
                  if (article.status === 'completed' && article.audio_url) {
                    e.stopPropagation();
                    if (isProUser) {
                      downloadArticle();
                    } else {
                      showUpgradeModal();
                    }
                  }
                }}
              />
            </Tooltip>
            <Tooltip title={
              !userStore.privacyMode && article.status === 'completed' ? "Share" : 
              userStore.privacyMode ? "Sharing disabled in privacy mode" : 
              "Share not available"
            }>
              <ShareIcon 
                data-interactive={!userStore.privacyMode && article.status === 'completed' ? "true" : "false"}
                style={{ 
                  fontSize: '18px', 
                  color: !userStore.privacyMode && article.status === 'completed' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.2)',
                  cursor: !userStore.privacyMode && article.status === 'completed' ? 'pointer' : 'default',
                  opacity: userStore.privacyMode ? 0.3 : 1
                }}
                onClick={(e) => {
                  if (!userStore.privacyMode && article.status === 'completed') {
                    e.stopPropagation();
                    shareArticle();
                  }
                }}
              />
            </Tooltip>
            <IconButton 
              data-interactive="true"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClick(e);
              }}
            >
              <MoreVertIcon style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '18px' }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              style={{ zIndex: 1001 }}
            >
              <MenuItem onClick={togglePlayed}>
                Mark As {article.played ? 'Unplayed' : 'Played'}
              </MenuItem>
              <MenuItem onClick={() => {
                handleMenuClose();
                window.open(`http://${article.article_id}`, '_blank');
              }}>
                View Original Text
              </MenuItem>
              {article.status === 'completed' && (
                <MenuItem onClick={() => {
                  handleMenuClose();
                  // Add report issues logic here
                }}>
                  Report Issues
                </MenuItem>
              )}
              <MenuItem onClick={deleteArticle}>Delete</MenuItem>
            </Menu>
          </div>
        </div>
      );
    };

    return (
      <div className="article-container" style={{ 
        width: isDesktop ? '90%' : '95%',
        maxWidth: isMobile ? '100%' : '90%',
        minWidth: isMobile ? '100%' : '800px',
        padding: '0',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: isDesktop ? 'center' : 'stretch'
              }}>

        {/* Remove the Add Article button since we're already on the add page */}
        {articles === null ? (
          <div className="feeds-loading-container" style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '100px 20px'
          }}>
            <Loader 
              size={60}
              fullScreen={false}
              backgroundColor="transparent"
            />
          </div>
        ) : filteredArticles.length > 0 ? (
          <>
            {isDesktop ? (
              // Desktop table layout
              <div className="desktop-table-container" style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <DesktopTableHeader />
                {visibleArticles
                  .filter(a => filteredArticles.some(fa => fa.article_id === a.article_id))
                  .map((article) => (
                    article.isLoader ? (
                      <DesktopArticleLoader key={article.article_id} />
                    ) : (
                      <DesktopTableRow key={article.article_id} article={article} />
                    )
                  ))}
                {visibleArticles.length < filteredArticles.length && (
                  <div ref={loadMoreRef} style={{ height: '20px' }} />
                )}
              </div>
            ) : (
              // Mobile layout (existing)
              <>
                {visibleArticles
                  .filter(a => filteredArticles.some(fa => fa.article_id === a.article_id))
                  .map((article) => (
                    article.isLoader ? (
                      <ArticleLoader key={article.article_id} url={article.article_url} />
                    ) : (
                      <PlaylistArticle
                        key={article.article_id}
                        {...{
                          article,
                          trackingId,
                          addArticleToPlaylist,
                          selectArticle,
                          togglePlayed,
                          removeArticle,
                          isActive: isPlaying && currentArticle && currentArticle.article_id === article.article_id,
                          showPlayedCheckbox: playlistMode === 'all' || playlistMode === 'played',
                        }}
                      />
                    )
                  ))}
                {visibleArticles.length < filteredArticles.length && (
                  <div ref={loadMoreRef} style={{ height: '20px' }} />
                )}
              </>
            )}
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            color: 'white',
            width: '100%',
            padding: '100px 20px'
          }}>
            {playlistSearchQuery ? (
              `No articles found matching "${playlistSearchQuery}". Try a different search term.`
            ) : (
              `You don't have any ${playlistMode === 'unplayed' ? 'unplayed' : 'played'} articles. To add some, <Link to="/">subscribe to a feed</Link> or <Link to="/add">add an article manually</Link>.`
            )}
          </div>
        )}
        
        {/* Upgrade Modal */}
        <Modal
          isOpen={isModalOpen}
          onDismiss={hideModal}
          isBlocking={false}
          isDarkOverlay={true}
          containerClassName={contentStyles.container}
        >
          {modalContent?.customContent || (
            <>
              {modalContent?.title && (
                <div style={{ color: 'white', marginBottom: '15px', fontWeight: 'bold' }}>
                  {modalContent.title}
                </div>
              )}
              {modalContent?.message && (
                <div style={{ color: 'white', marginBottom: '20px' }}>
                  {modalContent.message}
                </div>
              )}
              {modalContent?.actionText && modalContent?.actionHandler && (
                <button
                  onClick={modalContent.actionHandler}
                  className="clickable"
                  style={{
                    backgroundColor: '#1976d2',
                    border: 'none',
                    color: 'white',
                    borderRadius: '9px',
                    marginTop: '14px',
                    marginRight: '10px',
                    fontSize: '16px',
                    padding: '8px 16px',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1565c0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1976d2';
                  }}
                >
                  {modalContent.actionText}
                </button>
              )}
            </>
          )}
        </Modal>
      </div>
    );
  }
);

export default Articles;
